// src/components/StatusSelect.tsx
// Inline status selector with dropdown menu showing badge variants.

import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { cn } from '@core/ui';
import { AnchoredMenu } from './AnchoredMenu';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import type { BadgeProps } from '@react/components';
import {
  taskStatusMap,
  statusOptions,
  getStatusMapping,
  type BadgeVariant,
} from '@/features/task/logic';

export interface StatusSelectProps {
  /** Current status value (e.g. 'TASK_STATUS_IN_PROGRESS') */
  value: string;
  /** Callback when a new status is selected */
  onSelect: (status: string) => void;
  /** Disable the selector */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Badge-only trigger with no chevron — for dense rows, where one chevron per
   * task is fifty pieces of chrome the badge already implies.
   */
  compact?: boolean;
}

function mapVariant(variant: BadgeVariant): BadgeProps['variant'] {
  const valid: BadgeProps['variant'][] = [
    'default', 'primary', 'secondary', 'success', 'warning', 'error',
  ];
  return valid.includes(variant as any) ? (variant as BadgeProps['variant']) : 'default';
}

export function StatusSelect({
  value,
  onSelect,
  disabled = false,
  className,
  compact = false,
}: StatusSelectProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLElement>(null);
  const menuId = useId();

  const currentMapping = getStatusMapping(taskStatusMap, value);

  const close = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  // Outside click, Escape and repositioning live in AnchoredMenu; what stays
  // here is the option keyboard navigation and selection.

  useEffect(() => {
    if (open && listRef.current && focusedIndex >= 0) {
      const item = listRef.current.children[focusedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex, open]);

  const handleSelect = (statusValue: string) => {
    onSelect(statusValue);
    close();
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = statusOptions.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else if (focusedIndex >= 0) {
          handleSelect(statusOptions[focusedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) {
          setOpen(true);
          const idx = statusOptions.findIndex((o) => o.value === value);
          setFocusedIndex(idx >= 0 ? idx : 0);
        } else {
          setFocusedIndex((prev) =>
            prev + 1 < statusOptions.length ? prev + 1 : prev,
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (open) {
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;
    }
  };

  const dropdown = (
    <AnchoredMenu
      anchor={triggerRef}
      open={open}
      onClose={close}
      ref={listRef}
      as="ul"
      role="listbox"
      id={menuId}
      ariaLabel={t('task.field.status')}
      ariaActivedescendant={focusedIndex >= 0 ? `${menuId}-option-${focusedIndex}` : undefined}
      matchTriggerWidth
      maxHeight={280}
      className="py-1"
    >
        {statusOptions.map((option, index) => {
          const mapping = getStatusMapping(taskStatusMap, option.value);
          const isSelected = option.value === value;
          const isFocused = index === focusedIndex;
          return (
            <li
              key={option.value}
              id={`${menuId}-option-${index}`}
              role="option"
              aria-selected={isSelected}
              onMouseEnter={() => setFocusedIndex(index)}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(option.value);
              }}
              className={cn(
                'cursor-pointer select-none px-3 py-1.5 text-sm flex items-center gap-2',
                isFocused && 'bg-accent text-accent-foreground',
                isSelected && !isFocused && 'bg-accent/50',
              )}
            >
              <Badge variant={mapVariant(mapping.variant)} size="sm">
                {t(mapping.labelKey)}
              </Badge>
              {isSelected && (
                <svg className="h-4 w-4 ml-auto text-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </li>
          );
        })}
    </AnchoredMenu>
  );

  return (
    <div className={cn('inline-flex', className)}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            setOpen((prev) => !prev);
            if (!open) {
              const idx = statusOptions.findIndex((o) => o.value === value);
              setFocusedIndex(idx >= 0 ? idx : 0);
            }
          }
        }}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('task.field.status')}
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-sm',
          'hover:bg-accent transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        <Badge variant={mapVariant(currentMapping.variant)} size="sm">
          {t(currentMapping.labelKey)}
        </Badge>
        {!compact && (
          <svg
            className={cn(
              'h-3 w-3 text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </button>
      {dropdown}
    </div>
  );
}
